import type { SNTicketNote } from '@nsc/servicenow/types.js';
import { ServiceNowTicketNote, type ServiceNowTicket } from '../db/ServiceNowTicket.js';

/**
 * Work-note rendering shared by the colleague list and the FE's own ticket page, so both show the
 * same notes in the same way.
 */

/**
 * Allow-list sanitizer for ServiceNow note markup.
 *
 * Note text is third-party content: it reaches us from ServiceNow, which in turn takes it from Dell
 * and from customer-facing agents. It is rendered with lit's `unsafeHTML`, so anything not removed
 * here executes in the app. Everything outside the allow-list is dropped, including every attribute
 * (so no `href`, `src`, `style` or `on*` survives) — the notes only ever need inline emphasis,
 * lists and line breaks.
 */
const ALLOWED_NOTE_TAGS = new Set(['b', 'strong', 'i', 'em', 'u', 'br', 'p', 'ul', 'ol', 'li', 'span', 'div']);

/** Detects whether what's left after tag filtering is real markup or just plain text. */
const CONTAINS_MARKUP = /<(b|strong|i|em|u|br|p|ul|ol|li|span|div)\b[^>]*>/i;

export function sanitizeNoteHtml(raw: string): string {
  const withoutCodeMarkers = raw
    // ServiceNow wraps pasted rich text in BBCode-style `[code]…[/code]` markers. They are not
    // markup, carry no meaning for the reader, and would otherwise show up as literal text.
    .replace(/\[\/?code\]/gi, '');

  const withoutBlocks = withoutCodeMarkers
    // Drop script/style/iframe/object bodies outright — stripping only their tags would leave the
    // payload as visible text.
    .replace(/<(script|style|iframe|object|embed)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<(script|style|iframe|object|embed)\b[^>]*>/gi, '');

  const tagsFiltered = withoutBlocks.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (match, tagName: string) => {
    if (!ALLOWED_NOTE_TAGS.has(tagName.toLowerCase())) return '';
    // Re-emit the tag with no attributes at all.
    return match.startsWith('</') ? `</${tagName.toLowerCase()}>` : `<${tagName.toLowerCase()}>`;
  });

  // Newlines mean different things in the two kinds of note SN sends. In a plain-text note they are
  // the only line structure there is, so they become <br>. In an HTML note they are just source
  // formatting between tags — turning those into <br> is what put a blank line between every list
  // item — so they collapse to a single space instead.
  const withBreaks = CONTAINS_MARKUP.test(tagsFiltered)
    ? tagsFiltered.replace(/\s*\r?\n\s*/g, ' ')
    : tagsFiltered.replace(/\r?\n/g, '<br>');

  return trimTrailingBreaks(withBreaks);
}

/** Strip leading/trailing whitespace and the empty `<br>`/`<p></p>` runs SN leaves at both ends. */
function trimTrailingBreaks(html: string): string {
  let out = html.trim();
  let previous: string;
  do {
    previous = out;
    out = out
      .replace(/^(?:\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<div>\s*<\/div>)+/i, '')
      .replace(/(?:\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<div>\s*<\/div>)+$/i, '')
      .trim();
  } while (out !== previous);
  return out;
}

/**
 * Notes the platform writes about its own workflow state, not a person. On nsctest 92% of work notes
 * are of this kind ("Automatically set to Assigned…", "Chloe Unassigned engineer"), which buries the
 * few an engineer actually needs (a new contact phone, a door code).
 *
 * Matched against the note text AFTER the `[From Work Order …]` provenance line is removed, and
 * anchored at the start, so a human note that merely quotes one of these phrases still shows. The
 * list is deliberately narrow: hiding one useful note costs more than showing a line of noise. Add a
 * pattern here when NSC names another generator.
 */
const AUTOMATED_NOTE_PATTERNS: RegExp[] = [/^Automatically\b/i, /^Chloe\b/i];

/** Sanitized note HTML → the plain text a reader sees, minus the provenance line. */
function visibleText(html: string): string {
  return html
    .replace(/<\s*(br|\/p|\/div|\/li)\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/^\s*\[From Work Order [^\]]*\]\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isAutomatedNote(html: string): boolean {
  const text = visibleText(html);
  return AUTOMATED_NOTE_PATTERNS.some(pattern => pattern.test(text));
}

/**
 * How many notes a ticket shows: ONLY THE LAST ONE. A work order task collects a long history, and what an
 * engineer needs on site is the latest word on it — so every SmartTeams view (Tickets, ticket detail,
 * Colleagues) shows the newest note and nothing before it.
 */
export const MAX_NOTES_SHOWN = 1;

/**
 * Turn SN note rows into display-ready entries: the most recent one that is worth showing.
 *
 * Notes whose body sanitizes down to nothing are dropped — an empty bubble with only a timestamp is
 * noise — and so are the platform's own state-change notes (see {@link AUTOMATED_NOTE_PATTERNS}). The
 * "last note" is taken AFTER that, otherwise it would nearly always be a machine line that is then hidden and
 * leaves the ticket with nothing. ServiceNow offers no way to delete a note, so hiding is the only option.
 */
export function renderNotes(notes: SNTicketNote[]): ServiceNowTicketNote[] {
  return [...notes]
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    .map(note => new ServiceNowTicketNote({ date: note.date ?? '', html: sanitizeNoteHtml(note.value ?? '') }))
    .filter(note => (note.html ?? '').trim().length > 0)
    .filter(note => !isAutomatedNote(note.html ?? ''))
    .slice(0, MAX_NOTES_SHOWN);
}

/** Parallel `ticket_details` calls when loading work notes for a rendered list. */
const NOTES_CONCURRENCY = 6;

/**
 * Cap on how many tickets get their work notes loaded in one request. Notes are only available
 * from the per-ticket `ticket_details` endpoint, so each one is a round trip; beyond this the list
 * still renders, just without notes on the overflow.
 */
const NOTES_MAX_TICKETS = 60;

/**
 * Load work notes for the rendered tickets, in parallel and bounded.
 *
 * `ticket_list` never returns notes, so this costs one `ticket_details` call per ticket. A failed
 * lookup leaves that ticket's notes empty rather than failing the whole list — a missing note is a
 * far smaller problem than an empty page.
 */
export async function attachWorkNotes(tickets: ServiceNowTicket[]): Promise<void> {
  const targets = tickets.filter(t => t.number).slice(0, NOTES_MAX_TICKETS);
  if (targets.length === 0) return;

  const { getTicketNotes } = await import('@nsc/servicenow/smartteam-api.js');
  const queue = [...targets];

  const worker = async () => {
    for (let ticket = queue.shift(); ticket !== undefined; ticket = queue.shift()) {
      try {
        const { workNotes } = await getTicketNotes(ticket.number!);
        ticket.workNotes = renderNotes(workNotes);
      } catch {
        ticket.workNotes = [];
      }
    }
  };
  await Promise.all(Array.from({ length: NOTES_CONCURRENCY }, worker));
}
